import {
  Address,
  AddressDetailDto,
  HomeInformationInput,
  HomeInformationInputSchema,
  SelectedAddress,
  useAddressDetail,
} from '@applyonline/shared'
import {
  AddressAutoCompleteInput,
  FORM_ID,
  ScrollableAlert,
  useTranslatedOptionEnum,
} from '@applyonline/shared/components'
import { calculateTotalMonths, helpTip, setYearMonthValueFromMonths, useFormAutoSave } from '@applyonline/shared/utils'
import { yupResolver } from '@hookform/resolvers/yup'
import { EFinancingProgram, EHomeStatus, EProvince, getEHomeStatusList, getEProvinceList } from '@if/core/types'
import { formatAddress } from '@if/core/utils'
import { range } from 'lodash-es'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { getRecaptchaToken } from '../../../services/recaptcha'
import { useTrackFunnelStepReached } from '../../../services/trackingEvents'
import { Constants } from '../../../types/constants'
import { ETrackedEvents } from '../../../types/tracking'

declare global {
  interface Window {
    initAutocomplete: () => void
  }
}

interface Props {
  applicantName: string
  applicantBirthDate: string
  applicantData: HomeInformationInput
  forceProvince?: EProvince
  onApplicantChanged: (data: HomeInformationInput) => void
  nextStepAfter: (currentStep: string) => void
  currentStep: string
  monthlyIncome: number
  setDisableNext: (disable: boolean) => void
}

const AddressForm = ({
  forceProvince,
  applicantData,
  onApplicantChanged,
  nextStepAfter,
  currentStep,
  applicantName,
  applicantBirthDate,
  monthlyIncome,
  setDisableNext,
}: Props) => {
  useTrackFunnelStepReached(ETrackedEvents.Address, useParams().merchantId)
  const {
    t,
    i18n: { resolvedLanguage: lang },
  } = useTranslation()
  const yearSelectRef = useRef<HTMLSelectElement>(null)
  const [showAlert, setShowAlert] = useState(false)
  const [isEqualError, setIsEqualError] = useState(false)
  const [selectedAddress, setSelectedAddress] = useState<SelectedAddress | null>(null)
  const [showAutoCompleteAddressInput, setShowAutoCompleteAddressInput] = useState(true)
  const [addressDetail] = useAddressDetail(EFinancingProgram.Personal)
  const { executeRecaptcha } = useGoogleReCaptcha()

  const validationSchema = HomeInformationInputSchema(applicantBirthDate)
  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
    trigger,
    subscribe,
  } = useForm<HomeInformationInput>({
    mode: 'onBlur',
    resolver: yupResolver(validationSchema),
    defaultValues: applicantData,
  })

  useFormAutoSave(subscribe, onApplicantChanged, validationSchema)

  const onSubmit: SubmitHandler<HomeInformationInput> = (data) => {
    onApplicantChanged(data)
    nextStepAfter(currentStep)
  }

  useEffect(() => {
    if (applicantData.currentAddress.months > 0)
      setYearMonthValueFromMonths(applicantData.currentAddress.months, yearSelectRef.current!)

    helpTip()
  }, [applicantData.currentAddress.months, trigger])

  const totalMonthlyHomePayment = watch('spending.totalMonthlyHomePayment')
  const housingMonthly = watch('spending.housingMonthly')
  const civicNumber = watch('currentAddress.civicNumber')
  const apartmentNumber = watch('currentAddress.apartment')
  const homeFeeTypeId = useWatch({
    control,
    name: 'spending.homeFeeTypeId',
    defaultValue: applicantData.spending.homeFeeTypeId,
  })

  const needTotalMonthlyHomePayment = useCallback(() => {
    return homeFeeTypeId !== EHomeStatus.Owner && homeFeeTypeId !== EHomeStatus.WithRelative
  }, [homeFeeTypeId])

  const needMonthlyHomePayment = () => {
    return (
      (totalMonthlyHomePayment !== null && totalMonthlyHomePayment > 0) || homeFeeTypeId === EHomeStatus.WithRelative
    )
  }

  const housingExceedsIncome = useMemo(
    () =>
      monthlyIncome > 0 && Number(housingMonthly ?? 0) >= monthlyIncome * Constants.HousingToIncomeRatioThreshold,
    [housingMonthly, monthlyIncome],
  )

  const [previousHomeFeeTypeId, setPreviousHomeFeeTypeId] = useState<EHomeStatus | undefined>(homeFeeTypeId)
  useEffect(() => {
    if (homeFeeTypeId !== previousHomeFeeTypeId) {
      setValue('spending.totalMonthlyHomePayment', null, {
        shouldTouch: true,
        shouldDirty: true,
      })
      setValue('spending.housingMonthly', null, {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      })
    }
  }, [homeFeeTypeId, setValue, previousHomeFeeTypeId, needTotalMonthlyHomePayment])
  useEffect(() => {
    setPreviousHomeFeeTypeId(homeFeeTypeId)
  }, [setPreviousHomeFeeTypeId, homeFeeTypeId])

  const onChangeMonth = () => {
    const year = +(yearSelectRef.current?.value ?? 0)
    const newTotalMonths = calculateTotalMonths(year)

    setValue('currentAddress.months', newTotalMonths, { shouldValidate: true, shouldTouch: true, shouldDirty: true })

    if (newTotalMonths > 0) clearErrors('currentAddress.months')
  }

  const equalNumbers = useMemo(() => {
    return (
      civicNumber === apartmentNumber &&
      civicNumber !== '' &&
      apartmentNumber !== '' &&
      civicNumber !== undefined &&
      apartmentNumber !== undefined
    )
  }, [civicNumber, apartmentNumber])

  useEffect(() => {
    setDisableNext(equalNumbers)
    setShowAlert(true)
    setIsEqualError(equalNumbers)
  }, [equalNumbers, setDisableNext])

  const handleClearFields = () => {
    setShowAlert(false)
  }

  const onChangeAddress = async (address: SelectedAddress) => {
    if (!address) return
    const recaptchaToken = await getRecaptchaToken(executeRecaptcha, 'submit_addressDetail')
    const dto: AddressDetailDto = {
      placeId: address.placeId,
      recaptchaToken,
      userLanguageCode: lang!,
    }

    const ret = await addressDetail(dto)

    const currentAddress = {
      civicNumber: ret.address.civicNumber,
      street: ret.address.street,
      apartment: ret.address.apartment ?? '',
      postalCode: ret.address.postalCode,
      city: ret.address.city,
      stateIso: ret.address.stateIso,
      country: ret.address.country,
    }

    Object.entries(currentAddress).forEach(([key, value]) => {
      setValue(`currentAddress.${key as keyof Address}`, value, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      })
    })
  }

  const hasAddressAutoCompleteInputError = () => {
    if (!errors.currentAddress) return false

    const fieldsToCheck: (keyof typeof errors.currentAddress)[] = [
      'civicNumber',
      'street',
      'postalCode',
      'city',
      'stateIso',
    ]

    return fieldsToCheck.some((field) => errors.currentAddress?.[field])
  }
  const addressError = errors.currentAddress?.stateIso?.message

  const provinceOptions = useTranslatedOptionEnum('EProvince', getEProvinceList(), 'enum')
  const homeStatusOptions = useTranslatedOptionEnum('EHomeStatus', getEHomeStatusList(), 'enum')

  return (
    <section className="step-content">
      <h3 className="form-question">{t('address.provideAddress', { firstName: applicantName })}</h3>
      <ScrollableAlert
        type="error"
        showAlert={equalNumbers && showAlert}
        message={t('common.sameCivicAndAppartmentNumber')}
        buttons={[
          {
            id: 'btn-dismiss',
            label: t('common.yes'),
            onClick: () => {
              setShowAlert(false)
              setDisableNext(false)
              setIsEqualError(false)
            },
          },
          { id: 'btn-correct', label: t('common.modify'), onClick: () => handleClearFields() },
        ]}
      />
      {addressError && (
        <ScrollableAlert
          type="error"
          showAlert={addressError !== undefined && addressError !== ''}
          message={addressError}
        />
      )}
      <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID} noValidate>
        {showAutoCompleteAddressInput ? (
          <>
            <ScrollableAlert
              type="error"
              showAlert={!!errors.currentAddress?.country || hasAddressAutoCompleteInputError()}
              message={
                errors.currentAddress?.country
                  ? t('addressAutoComplete.canadaAddress')
                  : t('addressAutoComplete.refineSearch')
              }
            />
            <AddressAutoCompleteInput
              setShowAutoCompleteAddressInput={setShowAutoCompleteAddressInput}
              selectedAddress={selectedAddress}
              setSelectedAddress={setSelectedAddress}
              onChangeAddress={onChangeAddress}
              fullAddressDisplay={
                applicantData.currentAddress.civicNumber ? formatAddress(applicantData.currentAddress) : ''
              }
              financingProgramId={EFinancingProgram.Personal}
              getRecaptchaToken={getRecaptchaToken}
            />
          </>
        ) : (
          <>
            <div
              className={`control-group mid ${errors.currentAddress?.civicNumber ? 'error' : ''} ${isEqualError ? 'error' : ''}`}
            >
              <label htmlFor="civic_number">{t('address.addressNumber')}</label>
              <input
                type="number"
                inputMode="decimal"
                id="civicNumber"
                autoComplete="address-line1"
                {...register('currentAddress.civicNumber')}
                maxLength={10}
              />
            </div>

            <div className={`control-group mid ${errors.currentAddress?.street && 'error'}`}>
              <label htmlFor="street_name">{t('address.streetName')}</label>
              <input type="text" id="street" {...register('currentAddress.street')} maxLength={100} />
            </div>

            <div className={`control-group mid ${errors.currentAddress?.city && 'error'}`}>
              <label htmlFor="city">{t('address.city')}</label>
              <input type="text" id="city" {...register('currentAddress.city')} maxLength={50} />
            </div>

            <div className="control-group mid">
              <label htmlFor="select">{t('address.province')}</label>
              <div className={`dropdown-wrap ${errors.currentAddress?.stateIso && 'error'}`}>
                <select id="stateIso" {...register('currentAddress.stateIso')} defaultValue="">
                  {!forceProvince && (
                    <>
                      <option value="" disabled>
                        {' '}
                      </option>
                      {provinceOptions}
                    </>
                  )}
                  {forceProvince && (
                    <option value={forceProvince}>{t(`EProvince.${forceProvince}`, { ns: 'enum' })}</option>
                  )}
                </select>
              </div>
            </div>

            <div className={`control-group mid ${errors.currentAddress?.postalCode && 'error'}`}>
              <label htmlFor="postal_code">{t('address.postalCode')}</label>
              <input type="text" id="postalCode" {...register('currentAddress.postalCode')} maxLength={7} />
            </div>
          </>
        )}

        <div
          className={`control-group mid ${errors.currentAddress?.apartment ? 'error' : ''} ${isEqualError ? 'error' : ''}`}
        >
          <label htmlFor="apartment_number">{t('address.apartmentNumber')}</label>
          <input type="text" id="apartment" {...register('currentAddress.apartment')} maxLength={50} />
        </div>

        <div className="control-group" style={{ paddingBottom: 0 }}>
          <label htmlFor="year" style={{ marginBottom: 0 }}>
            {t('livingAtAddressSince.howLong')}
            <span className="help-tip">
              <p>{t('livingAtAddressSince.details')}</p>
            </span>
          </label>
        </div>

        <div className="control-group mid">
          <div className={`dropdown-wrap ${errors.currentAddress?.months ? 'error' : ''}`}>
            <select id="year" name="year" onChange={onChangeMonth} ref={yearSelectRef} defaultValue={0}>
              <option value={0}>{t('common.years')}</option>
              <option value={-1}>{t('common.lessThanOneYear')}</option>
              {range(1, 15).map((item) => (
                <option value={item} key={item}>
                  {t('common.yearsLabel', { count: item })}
                </option>
              ))}
              <option value={15}>
                {t('common.moreThan')} {t('common.yearsLabel', { count: 15 })}
              </option>
            </select>
          </div>
        </div>

        <div className="control-group" style={{ paddingBottom: 0 }}>
          <label htmlFor="housing_status" style={{ marginBottom: 0 }}>
            {t('housing.currentStatus')}
            <span className="help-tip">
              <p>{t('livingAtAddressSince.details')}</p>
            </span>
          </label>
        </div>

        <div className="control-group mid">
          <label htmlFor="housing_status">{t('housing.homeFeeType')}</label>
          <div className={`dropdown-wrap ${errors.spending?.homeFeeTypeId && 'error'}`}>
            <select {...register('spending.homeFeeTypeId')} id="homeFeeTypeId">
              {homeStatusOptions}
            </select>
          </div>
        </div>

        {needTotalMonthlyHomePayment() && (
          <div className={`control-group mid  ${errors.spending?.totalMonthlyHomePayment && 'error'}`}>
            <label htmlFor="mortgage_amount">
              {t('housing.totalMonthlyPayment', { homeFeeType: t(`housing.${homeFeeTypeId}`) })}
            </label>
            <div className={`number-wrap ${lang === 'en' ? 'startwith' : 'endwith'}`}>
              <input type="number" id="totalMonthlyHomePayment" {...register('spending.totalMonthlyHomePayment')} />
            </div>
          </div>
        )}
        {needMonthlyHomePayment() && (
          <div className={`control-group  ${errors.spending?.housingMonthly && 'error'}`}>
            <label htmlFor="mortgage_payment">
              {t('housing.contributedMonthlyPayment', {
                homeFeeType: t(`housing.${homeFeeTypeId}`),
              })}
            </label>
            <div className={`number-wrap ${lang === 'en' ? 'startwith' : 'endwith'}`}>
              <input type="number" id="monthlyHomePayment" {...register('spending.housingMonthly')} />
            </div>
            {housingExceedsIncome && (
              <p className="error-message" style={{ color: 'var(--alert-warning-color)' }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 5 }} />
                {t('housing.contributionExceedsIncomeWarning')}
              </p>
            )}
          </div>
        )}
      </form>
    </section>
  )
}

export default AddressForm
